import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import BodySmall from "../atoms/typography/BodySmall";
import { theme } from "../../theme";
import Small from "../atoms/typography/Small";

interface Props<T extends string> {
  tabs: Array<{
    tabLabelKey?: string;
    tabLabel?: React.ReactNode;
    id: T;
    color?: string;
  }>;
  selectedId: T;
  onSelectTabId: (id: T) => void;
  small?: boolean;
  fullWidth?: boolean;
}

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ListRow = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.grey05};
  justify-content: ${(props) =>
    props.fullWidth ? "space-around" : "flex-start"};
  overflow-x: auto;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ScrollIndicator = styled.div<{
  direction: "left" | "right";
  visible: boolean;
}>`
  position: absolute;
  top: 0;
  ${(props) => (props.direction === "left" ? "left: 0" : "right: 0")};
  height: 100%;
  width: 32px;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.direction === "left"
      ? `linear-gradient(to right, ${theme.colors.white}, transparent)`
      : `linear-gradient(to left, ${theme.colors.white}, transparent)`};
  pointer-events: none;
  z-index: 1;

  &::after {
    content: "${(props) => (props.direction === "left" ? "‹" : "›")}";
    font-size: 20px;
    color: ${theme.colors.textSecondary};
    font-weight: bold;
  }
`;

const ListTabBarItem = styled.button<{
  selected: boolean;
  small?: boolean;
  color?: string;
}>`
  position: relative;
  padding: ${(props) => (props.small ? "4px 8px" : "8px 16px")};
  padding-left: ${(props) =>
    props.color
      ? props.small
        ? "16px"
        : "24px"
      : props.small
        ? "8px"
        : "16px"};
  outline: none;
  border: none;
  background-color: transparent;
  border-bottom: ${(props) =>
    props.selected
      ? `2px solid ${theme.colors.primary}`
      : "2px solid transparent"};

  &:hover {
    cursor: pointer;
    border-bottom: 2px solid ${theme.colors.primary};
  }

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${(props) => (props.color ? props.color : "none")};
    display: ${(props) => (props.color ? "block" : "none")};
  }
`;

const ListTabBar = <T extends string>({
  tabs,
  selectedId,
  onSelectTabId,
  small,
  fullWidth,
}: Props<T>) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = () => {
    const element = scrollRef.current;
    if (!element) return;

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const hasOverflow = scrollWidth > clientWidth;

    setShowLeftArrow(hasOverflow && scrollLeft > 0);
    setShowRightArrow(
      hasOverflow && scrollLeft < scrollWidth - clientWidth - 1,
    );
  };

  useEffect(() => {
    checkScroll();
    const element = scrollRef.current;
    if (element) {
      element.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        element.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [tabs]);

  return (
    <ScrollContainer>
      <ScrollIndicator direction="left" visible={showLeftArrow} />
      <ListRow fullWidth={fullWidth} ref={scrollRef} role="tablist">
        {tabs.map((tab) => (
          <ListTabBarItem
            type="button"
            role="tab"
            aria-selected={tab.id === selectedId}
            selected={tab.id === selectedId}
            key={tab.id}
            color={tab.color}
            onClick={() => onSelectTabId(tab.id)}
            small={small}
          >
            {tab.tabLabel ? (
              tab.tabLabel
            ) : small ? (
              <Small
                fontweight={400}
                color={
                  selectedId === tab.id
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
              >
                {t(tab.tabLabelKey || "")}
              </Small>
            ) : (
              <BodySmall
                fontweight={500}
                color={
                  selectedId === tab.id
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
              >
                {t(tab.tabLabelKey || "")}
              </BodySmall>
            )}
          </ListTabBarItem>
        ))}
      </ListRow>
      <ScrollIndicator direction="right" visible={showRightArrow} />
    </ScrollContainer>
  );
};

export default ListTabBar;
